import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCurrency, getSession, saveSession, generateAgreementCode, type Company, type Charge } from '../lib/session.ts';
import { getJson } from '../lib/api.ts';

const ChargesPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(getSession());
  const [charges, setCharges] = useState<Array<Charge>>([]);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!session || !session.otp) {
      navigate('/identification');
    }
  }, [navigate, session]);

  useEffect(() => {
    if (!companyId || !session?.customerId) return;

    const fetchCharges = async () => {
      setLoaded(false);
      try {
        const data = await getJson<Array<Charge>>(
          `/api/companies/${companyId}/charges/${session.customerId}`
        );
        setCharges(data);
        const updatedSession = {
          ...session,
          selectedCharges: data.map((charge) => parseInt(charge.id)),
          selectedChargesTotalAmount: data.reduce((sum, charge) => sum + Number(charge.amount_negotiable), 0),
          selectedChargesLabAmount: data.reduce((sum, charge) => sum + Number(charge.lab_amount), 0)
        };
        setSession(updatedSession);
        saveSession(updatedSession);
      } catch (error) {
        console.error(error);
        setError((error as Error).message || 'Erro ao buscar débitos.');
      } finally {
        setLoaded(true);
      }
    };

    fetchCharges();
  }, [companyId, session?.customerId]);

  const handleSimulate = () => {
    if (!session || !companyId) return;

    const agreementCode = generateAgreementCode();
    const updatedSession = {
      ...session,
      agreementCode,
      companyId,
    };

    saveSession(updatedSession);
    setSession(updatedSession);
    navigate(`/agreement/${agreementCode}/simulation`);
  };

  if (!companyId) {
    return <p className="text-center text-slate-600">Empresa não encontrada.</p>;
  }

  return (
    <main className="space-y-8">
      <section className="rounded-3xl bg-white p-8 shadow-md shadow-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-500">{session?.selectedCompany?.name}</p>
            <h1 className="mt-2 text-3xl font-semibold text-red-600">Seus débitos</h1>
          </div>
          <div className="rounded-3xl bg-red-50 p-5 text-right">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Total</p>
            <p className="mt-2 text-3xl font-semibold text-red-600">{formatCurrency(session?.selectedChargesTotalAmount || 0)}</p>
          </div>
        </div>
      </section>

      <section className="grid gap-4">
        {charges.map((charge) => (
          <article key={charge.id} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-slate-500">{charge.identifier} - {charge.description}</p>

                {/* {charge.amount_realized > 0 && charge.amount_paid > 0 && (
                  <p className="mt-2 text-sm text-slate-500">Realizado: {formatCurrency(charge.amount_realized)} • Pago: {formatCurrency(charge.amount_paid)}</p>
                )}

                {charge.amount_realized <= 0 && charge.amount_paid <= 0 && charge.amount_open > 0 && (
                  <p className="mt-2 text-sm text-slate-500">Original: {formatCurrency(charge.amount_open)}</p>
                )} */}
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-slate-500">Valor Atualizado: {formatCurrency(Number(charge.amount_negotiable))}</p>
                {Number(charge.lab_amount) > 0 && (
                  <p className="text-sm text-slate-500">
                    Inclui prótese/aparelho complementar: {formatCurrency(Number(charge.lab_amount))}
                  </p>
                )}
              </div>
            </div>
          </article>
        ))}
      </section>

      {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {loaded && !error && charges.length === 0 && (
        <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Houve um problema para recuperar os tratamentos em atraso. Entre em contato com a unidade.
        </div>
      )}

      {charges.length > 0 && (
        <button
          onClick={handleSimulate}
          className="w-full rounded-2xl bg-red-600 px-5 py-4 text-sm font-semibold text-white shadow-lg shadow-slate-200 transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Simular acordo para este saldo
        </button>
      )}
    </main>
  );
};

export default ChargesPage;
